import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { arrayOf, bool, func, number, object, shape, string } from 'prop-types'
import { Alert, Checkbox, Table, Tag } from 'antd'
import { downloadCSV } from '../../../utils'
import { ImportModes } from '../../../utils/constants'

import '../../../assets/import-report.scss'

export const ImportStatuses = Object.freeze({
  CREATED: 'CREATED',
  UPDATED: 'UPDATED',
  UNCHANGED: 'UNCHANGED',
  ERROR: 'ERROR'
})

const STATUS_COLORS = Object.freeze({
  [ImportStatuses.CREATED]: 'green',
  [ImportStatuses.UPDATED]: 'blue',
  [ImportStatuses.ERROR]: 'red'
})

/** A subset counter counts again rows already counted by the others. */
export const COUNTERS = Object.freeze([
  { key: 'created', color: 'green' },
  { key: 'updated', color: 'blue' },
  { key: 'unchanged', color: 'grey' },
  { key: 'errors', color: 'red' }
])

const PAGE_SIZE = 50
const ROW_HEIGHT = 39
const MIN_BODY_HEIGHT = 120
const CHROME_HEIGHT = 460

const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/

const EMPTY_VALUE = '\u2014'
const ARROW = '\u2192'

export const translateCode = (code, t, prefix) => {
  const [key, ...rest] = String(code).split(':')
  const detail = rest.join(':')

  if (!CODE_PATTERN.test(key)) {
    return t(`${prefix}.errors.UNEXPECTED`)
  }

  return t(`${prefix}.errors.${key}`, { detail })
}

const printed = value => (value === null || value === undefined || value === '') ? EMPTY_VALUE : value

/** A creation has nothing to compare to, so only an update spells out where the cell comes from. */
const showsOldValue = item => item.status === ImportStatuses.UPDATED

const changesText = item => (item.changes || [])
  .map(change => showsOldValue(item)
    ? `${change.column}: ${printed(change.from)} ${ARROW} ${printed(change.to)}`
    : `${change.column}: ${printed(change.to)}`
  )
  .join(' \u00b7 ')

const reasons = (item, t, prefix) => {
  return [item.errors, item.warnings]
    .filter(value => typeof value === 'string' && value !== '')
    .flatMap(value => value.split(', '))
    .filter(code => code !== '')
    .map(code => translateCode(code, t, prefix))
    .join(' · ')
}

const ImportReport = ({ counters = COUNTERS, entity, height, identify, prefix, report, t }) => {
  const isPreview = report.mode === ImportModes.DRY_RUN
  const summary = report.summary
  const missing = report.columns ? report.columns.missing : []

  const [errorsOnly, setErrorsOnly] = useState(summary.errors > 0)

  useEffect(() => setErrorsOnly(summary.errors > 0), [report, summary.errors])

  const rows = useMemo(
    () => errorsOnly ? report.data.filter(item => item.status === ImportStatuses.ERROR) : report.data,
    [errorsOnly, report.data]
  )

  const bodyHeight = useMemo(() => Math.max(
    MIN_BODY_HEIGHT,
    Math.min(Math.min(rows.length, PAGE_SIZE) * ROW_HEIGHT, height - CHROME_HEIGHT)
  ), [height, rows.length])

  const columns = useMemo(() => [
    {
      title: t(`${prefix}.report.line`),
      dataIndex: 'line',
      width: 70
    },
    ...(identify ? [{
      title: t(`${prefix}.report.identity`),
      key: 'identity',
      width: 260,
      render: (value, item) => {
        const { title, detail } = identify(item)

        return (
          <div className='import-identity'>
            <div className='title'>{printed(title)}</div>
            {detail && detail !== title && <div className='detail'>{detail}</div>}
          </div>
        )
      }
    }] : []),
    {
      title: t(`${prefix}.report.status`),
      dataIndex: 'status',
      width: 130,
      render: status => (
        <Tag color={STATUS_COLORS[status]}>
          {t(`${prefix}.status.${isPreview ? 'preview' : 'done'}.${status}`)}
        </Tag>
      )
    },
    {
      title: t(`${prefix}.report.changes`),
      dataIndex: 'changes',
      render: (changes, item) => (
        <ul className='import-changes'>
          {(changes || []).map(change => (
            <li key={change.column}>
              <span className='column'>{t(`${prefix}.columns.${change.column}`)}</span>
              {showsOldValue(item) && (
                <>
                  <span className='from'>{printed(change.from)}</span>
                  <span className='arrow'>{ARROW}</span>
                </>
              )}
              <span className='to'>{printed(change.to)}</span>
            </li>
          ))}
        </ul>
      )
    },
    {
      title: t(`${prefix}.report.reasons`),
      key: 'reasons',
      render: (value, item) => reasons(item, t, prefix)
    }
  ], [identify, isPreview, prefix, t])

  const exportReport = useCallback(() => {
    downloadCSV(report.data.map(item => ({
      [t(`${prefix}.report.line`)]: item.line,
      [t(`${prefix}.report.status`)]: t(`${prefix}.status.${isPreview ? 'preview' : 'done'}.${item.status}`),
      [t(`${prefix}.report.id`)]: item.id,
      [t(`${prefix}.report.changes`)]: changesText(item),
      [t(`${prefix}.report.reasons`)]: reasons(item, t, prefix),
      ...Object.keys(item[entity]).reduce((cells, column) => {
        cells[t(`${prefix}.columns.${column}`)] = item[entity][column]

        return cells
      }, {})
    })))
  }, [entity, isPreview, prefix, report.data, t])

  return (
    <div className='import-report'>
      <Alert
        type={isPreview ? 'info' : (summary.errors > 0 ? 'warning' : 'success')}
        message={t(isPreview ? `${prefix}.preview.headline` : `${prefix}.done.headline`)}
        showIcon
        style={{ marginBottom: '12px' }}
      />
      {missing.length > 0 && (
        <Alert
          type='warning'
          message={t(`${prefix}.missing_columns.title`)}
          description={
            <>
              <div>{missing.map(column => t(`${prefix}.columns.${column}`)).join(', ')}</div>
              <div>{t(`${prefix}.missing_columns.hint`)}</div>
            </>
          }
          showIcon
          style={{ marginBottom: '12px' }}
        />
      )}
      <div className='import-total'>{t(`${prefix}.summary.total`, { count: summary.total })}</div>
      <div className='import-counters'>
        {counters.map(counter => (
          <div
            key={counter.key}
            className={`import-counter ${counter.color}${counter.subset ? ' subset' : ''}${summary[counter.key] > 0 ? '' : ' empty'}`}
          >
            <div className='value'>{summary[counter.key]}</div>
            <div className='label'>{t(`${prefix}.summary.${counter.key}`)}</div>
          </div>
        ))}
      </div>
      {summary.errors > 0 && (
        <Checkbox
          checked={errorsOnly}
          onChange={event => setErrorsOnly(event.target.checked)}
          style={{ marginBottom: '8px' }}
        >
          {t(`${prefix}.report.errors_only`)}
        </Checkbox>
      )}
      <Table
        columns={columns}
        dataSource={rows}
        rowKey='line'
        size='small'
        pagination={{ pageSize: PAGE_SIZE, size: 'small' }}
        scroll={{ y: bodyHeight }}
      />
      <a onClick={exportReport}>{t(`${prefix}.report.export`)}</a>
    </div>
  )
}

ImportReport.propTypes = {
  counters: arrayOf(shape({ key: string.isRequired, color: string.isRequired, subset: bool })),
  entity: string.isRequired,
  height: number.isRequired,
  identify: func,
  prefix: string.isRequired,
  report: object.isRequired,
  t: func.isRequired
}

export default ImportReport
